import React, { FC, useEffect, useState } from 'react';

import { Listing as ListingType } from '../Listings/Listings.types';

import { getTranslator } from '../../shared/i18n/getTranslator';
import { StyledListingCard } from './Listings.styles';

import {
    agentLogoTracking,
    agentPhoneTracking,
    agentContactTracking
} from './Listings.tracking';

import {
    SearchPageType,
    Favourites,
    AnalyticsTaxonomy
} from '../SearchContext';
import searchTranslations from 'public/locales/en/search.json';
import { useInView } from 'react-intersection-observer';

const t = getTranslator(searchTranslations);

const removeFavouriteLabel = t('listingCard.favouriteButton.removeLabel');
const saveFavouriteLabel = t('listingCard.favouriteButton.saveLabel');
const viewsLabel = t('listingCard.views');
const viewsPeriodLabel = t('listingCard.30days');

type Props = {
    key: string;
    dataTestId: string;
    index: number;
    searchIdentifier: string;
    searchType: SearchPageType;
    overrideViewsWithAvailability: boolean;
    favouritesListings: Favourites['listings'];
    saveListing: (listingId: string) => Promise<boolean>;
    onImageClick: () => void;
    onCardClick: () => void;
    onListingView: () => void;
    analyticsTaxonomy: AnalyticsTaxonomy | undefined;
    adSlot?: JSX.Element | false;
} & ListingType;

export const Listing: FC<Props> = (props) => {
    const {
        listingId,
        flag,
        underOffer,
        saveListing,
        onImageClick,
        onCardClick,
        onListingView,
        availableFrom,
        availableFromLabel,
        priceDrop,
        index,
        searchIdentifier,
        searchType,
        overrideViewsWithAvailability,
        address,
        branch,
        features,
        highlights,
        image,
        isPremium,
        listingUris,
        numberOfFloorPlans,
        numberOfImages,
        numberOfVideos,
        numberOfViews,
        otherPropertyImages,
        price,
        priceTitle,
        publishedOn,
        publishedOnLabel,
        tags,
        title,
        transports,
        dataTestId,
        favouritesListings,
        adSlot
    } = props;

    const [isFavourite, setIsFavourite] = useState(false);
    const { ref, inView } = useInView({
        triggerOnce: true
    });

    useEffect(() => {
        const favourite = favouritesListings.includes(listingId);

        setIsFavourite(favourite);
    }, [favouritesListings]);

    useEffect(() => {
        if (inView) onListingView();
    }, [inView]);

    return (
        <div
            key={`listing_${listingId}`}
            data-testid={`${dataTestId}_listing_${listingId}`}
            id={`listing_${listingId}`}
            ref={ref}
        >
            <StyledListingCard
                data-testid={dataTestId}
                useLazyImage={index >= 1}
                flag={flag}
                underOffer={underOffer}
                isHelptoBuy={false}
                isFavourite={isFavourite}
                saveListing={() => saveListing(listingId)}
                contactOnClick={agentContactTracking}
                phoneOnClick={agentPhoneTracking}
                agentLogoOnClick={agentLogoTracking}
                imageOnClick={onImageClick}
                cardOnClick={onCardClick}
                removeFavouriteLabel={removeFavouriteLabel}
                saveFavouriteLabel={saveFavouriteLabel}
                viewsLabel={viewsLabel}
                viewsPeriodLabel={viewsPeriodLabel}
                searchIdentifier={searchIdentifier}
                availableFrom={availableFrom}
                availableFromLabel={availableFromLabel}
                searchType={searchType}
                overrideViewsWithAvailability={overrideViewsWithAvailability}
                priceDrop={priceDrop}
                address={address}
                branch={branch}
                features={features}
                highlights={highlights}
                image={image}
                isPremium={isPremium}
                listingUris={listingUris}
                numberOfFloorPlans={numberOfFloorPlans}
                numberOfImages={numberOfImages}
                numberOfVideos={numberOfVideos}
                numberOfViews={numberOfViews}
                otherPropertyImages={otherPropertyImages}
                price={price}
                priceTitle={priceTitle}
                publishedOn={publishedOn}
                publishedOnLabel={publishedOnLabel}
                tags={tags}
                title={title}
                transports={transports}
            />
            {adSlot}
        </div>
    );
};
